import React, { createContext, useContext, useReducer, useEffect, ReactNode } from 'react';
import { AuthState, AuthUser, UserType } from '../types/auth';
import { authService } from '../services/authService';
import { phoneToNumber } from '../utils/phoneFormatter';
import { createAuthTokenWithPhone } from '../utils/authToken';
import { apiService } from '../services/api';
import { organizationService } from '../services/organizationService';

// Actions для reducer
type AuthAction =
  | { type: 'LOGIN_START' }
  | { type: 'LOGIN_SUCCESS'; payload: AuthUser }
  | { type: 'LOGIN_FAILURE'; payload: string }
  | { type: 'LOGOUT' }
  | { type: 'CLEAR_ERROR' };

// Начальное состояние
const initialState: AuthState = {
  user: null,
  isLoading: false,
  error: null
};

// Reducer для управления состоянием
const authReducer = (state: AuthState, action: AuthAction): AuthState => {
  switch (action.type) {
    case 'LOGIN_START':
      return {
        ...state,
        isLoading: true,
        error: null
      };
    case 'LOGIN_SUCCESS':
      return {
        ...state,
        user: action.payload,
        isLoading: false,
        error: null
      };
    case 'LOGIN_FAILURE':
      return {
        ...state,
        user: null,
        isLoading: false,
        error: action.payload
      };
    case 'LOGOUT':
      return {
        ...state,
        user: null,
        isLoading: false,
        error: null
      };
    case 'CLEAR_ERROR':
      return {
        ...state,
        error: null
      };
    default:
      return state;
  }
};

// Интерфейс контекста
interface AuthContextType {
  state: AuthState;
  loginExecutor: (credentials: { phone: string }) => Promise<{ needsVerification: boolean; challengeToken?: string; error?: string }>;
  verifyExecutor: (credentials: { phone: string; code: string; challengeToken: string }) => Promise<void>;
  loginBusiness: (credentials: { phone: string }) => Promise<{ needsVerification: boolean; authUrl?: string; error?: string }>;
  verifyBusiness: (credentials: { phone: string; code: string }) => Promise<void>;
  logout: () => void;
  clearError: () => void;
  refreshToken: () => Promise<boolean>;
}

// Создание контекста
const AuthContext = createContext<AuthContextType | undefined>(undefined);

// Provider компонент
interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [state, dispatch] = useReducer(authReducer, initialState);

  // Проверка токена при загрузке
  useEffect(() => {
    const initAuth = async () => {
      const storedUser = localStorage.getItem('auth_user');
      const tokens = apiService.getTokens(); // Use apiService to get tokens
      
      if (storedUser && tokens?.accessToken) {
        try {
          const user = JSON.parse(storedUser);
          // Ensure user.manager.roles is an array if it's a string
          if (user.type === UserType.MANAGER && typeof user.manager?.roles === 'string') {
            user.manager.roles = [user.manager.roles];
          }
          dispatch({ type: 'LOGIN_SUCCESS', payload: user });
          
          // Проверяем валидность токена
          const isValid = await refreshToken();
          if (!isValid) {
            logout();
          }
        } catch (error) {
          logout();
        }
      }
    };

    initAuth();
  }, []);

  // Авторизация исполнителя (через ФНС)
  const loginExecutor = async (credentials: { phone: string }) => {
    dispatch({ type: 'LOGIN_START' });
    
    try {
      const phoneNumber = phoneToNumber(credentials.phone);
      const authToken = createAuthTokenWithPhone(phoneNumber);
      const response = await authService.loginUser({ phone: phoneNumber, authToken });
      
      if (response.result === 'VerifyCode') {
        return { needsVerification: true, challengeToken: response.challengeToken };
      } else if (response.result === 'Authorized' && response.accessToken && response.user) {
        const authUser: AuthUser = {
          type: UserType.USER,
          user: response.user,
          accessToken: response.accessToken,
          refreshToken: response.refreshToken!
        };
        
        localStorage.setItem('auth_user', JSON.stringify(authUser));
        // Use apiService to set tokens and user type
        apiService.setTokens({ accessToken: response.accessToken, refreshToken: response.refreshToken! });
        apiService.setUserType(UserType.USER);
        
        dispatch({ type: 'LOGIN_SUCCESS', payload: authUser });
        return { needsVerification: false };
      } else {
        throw new Error('Неожиданный ответ сервера');
      }
    } catch (error: any) {
      const errorMessage = error.response?.data?.message || error.message || 'Ошибка авторизации';
      dispatch({ type: 'LOGIN_FAILURE', payload: errorMessage });
      return { needsVerification: false, error: errorMessage };
    }
  };

  // Подтверждение кода исполнителя
  const verifyExecutor = async (credentials: { phone: string; code: string; challengeToken: string }) => {
    dispatch({ type: 'LOGIN_START' });
    
    try {
      const phoneNumber = phoneToNumber(credentials.phone);
      const response = await authService.verifyUser({ phone: phoneNumber, code: credentials.code, challengeToken: credentials.challengeToken });
      
      if (response.result === 'Authorized' && response.accessToken && response.user) {
        const authUser: AuthUser = {
          type: UserType.USER,
          user: response.user,
          accessToken: response.accessToken,
          refreshToken: response.refreshToken!
        };
        
        localStorage.setItem('auth_user', JSON.stringify(authUser));
        // Use apiService to set tokens and user type
        apiService.setTokens({ accessToken: response.accessToken, refreshToken: response.refreshToken! });
        apiService.setUserType(UserType.USER);
        
        dispatch({ type: 'LOGIN_SUCCESS', payload: authUser });
      } else {
        throw new Error('Неверный код или ошибка авторизации');
      }
    } catch (error: any) {
      const errorMessage = error.response?.data?.message || error.message || 'Ошибка подтверждения';
      dispatch({ type: 'LOGIN_FAILURE', payload: errorMessage });
      throw new Error(errorMessage);
    }
  };

  // Авторизация бизнеса (через MTS ID)
  const loginBusiness = async (credentials: { phone: string }) => {
    dispatch({ type: 'LOGIN_START' });
    
    try {
      const phoneNumber = phoneToNumber(credentials.phone);
      const authToken = createAuthTokenWithPhone(phoneNumber);
      const response = await authService.loginManager({ phone: phoneNumber, authToken });
      
      if (response.result === 'VerifyCode' && response.url) {
        // Сразу перенаправляем на MTS ID без промежуточного экрана
        window.location.href = response.url;
        return { needsVerification: true, authUrl: response.url };
      } else {
        throw new Error('Ошибка получения ссылки авторизации');
      }
    } catch (error: any) {
      const errorMessage = error.response?.data?.message || error.message || 'Ошибка авторизации';
      dispatch({ type: 'LOGIN_FAILURE', payload: errorMessage });
      return { needsVerification: false, error: errorMessage };
    }
  };

  // Подтверждение авторизации бизнеса (для менеджеров через MTS ID)
  const verifyBusiness = async (credentials: { phone: string; code: string }) => {
    dispatch({ type: 'LOGIN_START' });
    
    try {
      const phoneNumber = phoneToNumber(credentials.phone);
      // Для менеджеров код из URL параметра используется как challengeToken
      const response = await authService.verifyManager({ 
        phone: phoneNumber, 
        code: credentials.code, 
        challengeToken: credentials.code // Используем code как challengeToken для MTS ID флоу
      });
      
      if (response.result === 'Authorized' && response.accessToken && response.manager) {
        // Create a temporary authUser to store tokens and user type immediately
        const tempAuthUser: AuthUser = {
          type: UserType.MANAGER,
          manager: response.manager,
          accessToken: response.accessToken,
          refreshToken: response.refreshToken!
        };
        localStorage.setItem('auth_user', JSON.stringify(tempAuthUser));
        apiService.setTokens({ accessToken: response.accessToken, refreshToken: response.refreshToken! });
        apiService.setUserType(UserType.MANAGER);

        // Fetch all organizations and filter by managerId
        const allOrganizations = await organizationService.getOrganizations();
        const managerOrganizations = allOrganizations.filter(org => response.manager && org.managerId === response.manager.id);
        
        let managerWithOrgId = { ...response.manager };

        if (managerOrganizations && managerOrganizations.length > 0) {
          managerWithOrgId.organizationId = managerOrganizations[0].id;
        }

        const authUser: AuthUser = {
          type: UserType.MANAGER,
          manager: {
            ...managerWithOrgId,
            roles: typeof managerWithOrgId.roles === 'string' ? [managerWithOrgId.roles] : managerWithOrgId.roles
          },
          accessToken: response.accessToken,
          refreshToken: response.refreshToken!
        };
        
        // Update auth_user in localStorage with potentially updated manager object (including organizationId)
        localStorage.setItem('auth_user', JSON.stringify(authUser));
        // Dispatch LOGIN_SUCCESS with the final authUser
        dispatch({ type: 'LOGIN_SUCCESS', payload: authUser });
      } else if (response.result === 'WrongCode') {
        throw new Error('Неверный код авторизации. Попробуйте войти заново.');
      } else {
        throw new Error('Ошибка авторизации через MTS ID');
      }
    } catch (error: any) {
      const errorMessage = error.response?.data?.message || error.message || 'Ошибка подтверждения';
      dispatch({ type: 'LOGIN_FAILURE', payload: errorMessage });
      throw new Error(errorMessage);
    }
  };

  // Выход
  const logout = () => {
    localStorage.removeItem('auth_user');
    apiService.logout(); // Use apiService to clear tokens and user type
    dispatch({ type: 'LOGOUT' });
  };

  // Очистка ошибки
  const clearError = () => {
    dispatch({ type: 'CLEAR_ERROR' });
  };

  // Обновление токена
  const refreshToken = async (): Promise<boolean> => {
    try {
      const tokens = apiService.getTokens(); // Use apiService to get tokens
      
      if (!tokens?.accessToken || !tokens?.refreshToken) return false;

      let response;
      if (apiService.isManager()) { // Use apiService to determine user type
        response = await authService.refreshManagerToken(tokens.accessToken, tokens.refreshToken);
      } else {
        response = await authService.refreshToken(tokens.accessToken, tokens.refreshToken);
      }
      
      if (response.accessToken && response.refreshToken) {
        apiService.setTokens({ accessToken: response.accessToken, refreshToken: response.refreshToken });
        return true;
      }
      
      return false;
    } catch (error) {
      return false;
    }
  };

  const value: AuthContextType = {
    state,
    loginExecutor,
    verifyExecutor,
    loginBusiness,
    verifyBusiness,
    logout,
    clearError,
    refreshToken
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

// Hook для использования контекста
export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};